import { itemAtivo, pontuarNav, trilhaPara, type TrilhaItem } from '../../lib/trilha-nav';
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import {
  IconChevronRight,
  IconLogout,
  IconMoon,
  IconSearch,
  IconSelector,
  IconSettings,
  IconSun,
  IconUser,
  IconLayoutSidebarLeftCollapse,
} from '@tabler/icons-react';
import { useAuthStore } from '../../store/auth.store';
import { useAcessoStore, temAtribuicaoRbac, type AcessoResumo } from '../../store/acesso.store';
import { useEstabelecimentoAtivoStore } from '../../store/estabelecimento-ativo.store';
import { usePermissao } from '../../hooks/use-permissao';
import { useModulosStore } from '../../store/modulos.store';
import { useBrandingStore } from '../../store/branding.store';
import { useEmpresaIdentidadeStore } from '../../store/empresa-identidade.store';
import { useSidebarCollapse } from '../../lib/use-sidebar-collapse';
import { useTheme } from '../../lib/use-theme';
import { broadcastAuth } from '../../lib/auth-broadcast';
import { resolverUrlEstatica, apiFetch } from '../../lib/api';
import { deveMostrarSeletorEstabelecimento } from '../../lib/seletor-estabelecimento-rotas';
import { ehSubGrupo, folhasDoGrupo, type NavFilho, type NavItem, type NavSubGrupo, type NavSubItem } from '../../lib/nav-config';
import { SinoNotificacoes } from '../SinoNotificacoes';
import { ErrorBoundaryDeRota } from '../ErrorBoundary';
import { SeletorEstabelecimento } from '../SeletorEstabelecimento';
import { Avatar } from '../ui/Avatar';
import { Dropdown, MenuItem, MenuSeparator } from '../ui/Dropdown';
import { cn } from '../ui/cn';
import { chaveSubmenu } from '../../lib/chave-submenu';

/**
 * AppShell — Design System v2 [UI-F0] (docs/design-system-v2.html §4).
 *
 * Estrutura:
 *   ┌─────────┬─────────────────────────────────────┐
 *   │ sidebar │ topbar (busca + ícones + user chip)│
 *   │         ├─────────────────────────────────────┤
 *   │         │ subbar (logo+nome do tenant; só tenant)   │
 *   │         ├─────────────────────────────────────┤
 *   │         │ breadcrumb + <Outlet/> conteúdo     │
 *   └─────────┴─────────────────────────────────────┘
 *
 * Sidebar tem dois modos:
 *  - aberta (264 px): label + ícone, submenus em accordion.
 *  - colapsada (72 px): só ícone; grupos com children mostram flyout no hover.
 *
 * O shell é **agnóstico de rota** — recebe um `nav: NavItem[]` e o
 * `Outlet` do react-router se encarrega de renderizar a tela ativa.
 * AdminLayout e TenantLayout são thin wrappers que escolhem o nav.
 *
 * Fase 0 NÃO troca o router; o shell fica pronto pra ser plugado nas
 * rotas existentes em fases posteriores (basta envolver os routes
 * `/admin/*` ou `/dashboard, /minha-conta, /settings/*` em
 * `<Route element={<AdminLayout/>}>`).
 */

/**
 * [F4.3 #890] Rótulo do perfil no header: sem atribuição RBAC (pré-backfill), mostra o
 * enum legado como sempre (compatibilidade). Com atribuição: 1 só → nome do perfil, com
 * a unidade se for escopo de estabelecimento específico ("Gestor · Terrazo"); 2+ →
 * "N perfis" (o usuário abre "Meu perfil" pra ver o detalhe).
 */
function rotuloPerfil(perfilLegado: string, acesso: AcessoResumo | null): string {
  if (!temAtribuicaoRbac(acesso)) return perfilLegado;
  const atribuicoes = acesso!.atribuicoes;
  if (atribuicoes.length > 1) return `${atribuicoes.length} perfis`;
  const a = atribuicoes[0]!;
  return a.estabelecimento ? `${a.perfil.nome} · ${a.estabelecimento.nome}` : a.perfil.nome;
}

interface AppShellProps {
  nav: NavItem[];
  /**
   * Texto exibido como nome do produto no canto da sidebar. Quando omitido
   * (caso padrão), lê `app_nome` do `useBrandingStore` (SystemSettings →
   * env → "[Nome do produto]") e adiciona o sufixo " — Admin" se o usuário
   * for SUPER_ADMIN. Passar explicitamente sobrescreve toda a lógica.
   */
  brand?: string;
  /** Quando true, renderiza children em vez de Outlet (útil pra storybook/testes). */
  children?: ReactNode;
}

export function AppShell({ nav, brand, children }: AppShellProps): JSX.Element {
  const { collapsed } = useSidebarCollapse();
  const { theme, toggleTheme } = useTheme();
  const usuario = useAuthStore((s) => s.usuario);
  const acesso = useAcessoStore((s) => s.acesso);
  const appNome = useBrandingStore((s) => s.app_nome);
  const empresaNome = useEmpresaIdentidadeStore((s) => s.nome_empresa);
  const empresaLogoUrl = useEmpresaIdentidadeStore((s) => s.logo_url);

  // [#916] Estabelecimento ativo global — único `<SeletorEstabelecimento>` do
  // app, montado aqui. Toda tela que antes mantinha o próprio filtro de
  // unidade agora só lê `useEstabelecimentoAtivoStore`.
  const estabelecimentoAtivoId = useEstabelecimentoAtivoStore((s) => s.estabelecimentoAtivoId);
  const definirEstabelecimentoAtivo = useEstabelecimentoAtivoStore((s) => s.definir);
  const accessToken = useAuthStore((s) => s.access_token);
  const refreshToken = useAuthStore((s) => s.refresh_token);
  const setSession = useAuthStore((s) => s.setSession);

  // [#916 / ADR-030 decisão 2] Persiste a escolha como `estabelecimento_preferencial_id`
  // (reaproveitado — mesmo campo que a aba "Dados pessoais" já gravava via AB#185) pra
  // restaurar no próximo login. Só dispara PATCH quando o valor realmente muda —
  // `SeletorEstabelecimento` também chama isso ao aplicar o preferencial/single-unit no
  // mount, e nesses casos o valor já bate com o que está salvo (no-op local, sem request).
  function trocarEstabelecimentoAtivo(id: number | null): void {
    definirEstabelecimentoAtivo(id);
    const salvo = usuario?.estabelecimento_preferencial_id ?? null;
    if (id === salvo) return;
    void apiFetch('/auth/perfil', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ estabelecimento_preferencial_id: id }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { estabelecimento_preferencial_id?: number | null } | null) => {
        if (!data || !usuario || !accessToken || !refreshToken) return;
        setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
          usuario: { ...usuario, estabelecimento_preferencial_id: data.estabelecimento_preferencial_id },
        });
      })
      .catch(() => {
        // best-effort: a unidade ativa já trocou na sessão atual; só a
        // persistência pro próximo login falhou (sem toast — não é uma ação
        // explícita de "salvar", é um efeito colateral da troca no topo).
      });
  }

  // [Bug#830] Publica no <html> a altura REAL da SUBBAR (`--subbar-real-h`, 0 quando não renderiza —
  // SUPER_ADMIN/sem identidade) e do breadcrumb do topo do conteúdo (`--breadcrumb-real-h`, com o
  // espaço abaixo dele). Páginas de altura total (Inbox, Kanban) descontam esses valores — mesmo
  // padrão do `--banners-h` (BannersGlobais, App.tsx). O breadcrumb pode quebrar em várias linhas, e
  // a SUBBAR some sem identidade: por isso é medido, não hardcoded.
  const subbarRef = useRef<HTMLDivElement>(null);
  const breadcrumbRef = useRef<HTMLDivElement>(null);
  const temSubbar = Boolean(empresaNome);
  useLayoutEffect(() => {
    const raiz = document.documentElement;
    const alvos: Array<[HTMLElement | null, string]> = [
      [subbarRef.current, '--subbar-real-h'],
      [breadcrumbRef.current, '--breadcrumb-real-h'],
    ];
    const observers: ResizeObserver[] = [];
    for (const [el, nome] of alvos) {
      if (!el) {
        raiz.style.setProperty(nome, '0px');
        continue;
      }
      const publicar = (): void => raiz.style.setProperty(nome, `${el.offsetHeight}px`);
      publicar();
      if (typeof ResizeObserver !== 'undefined') {
        const obs = new ResizeObserver(publicar);
        obs.observe(el);
        observers.push(obs);
      }
    }
    return () => {
      for (const o of observers) o.disconnect();
      for (const [, nome] of alvos) raiz.style.removeProperty(nome);
    };
  }, [temSubbar]);
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  // Brand dinâmico: SUPER_ADMIN ganha sufixo "— Admin" no nome do produto.
  // [UI-TENANT] tira o hardcode "ToolCraftLab — Admin" e segue o branding.
  const brandResolvido =
    brand ?? (usuario?.perfil === 'SUPER_ADMIN' ? `${appNome} — Admin` : appNome);

  const breadcrumb = useMemo(() => trilhaPara(pathname, search, nav), [pathname, search, nav]);

  return (
    <div className="flex min-h-screen">
      {/* SIDEBAR */}
      <Sidebar nav={nav} brand={brandResolvido} collapsed={collapsed} pathname={pathname} />

      {/* MAIN */}
      <div
        className="flex-1 flex flex-col min-w-0 transition-[margin-left] duration-[260ms] ease-[var(--ease)]"
        style={{ marginLeft: collapsed ? 'var(--sidebar-w-collapsed)' : 'var(--sidebar-w)' }}
      >
        {/* TOPBAR */}
        <header
          className="h-[var(--topbar-h)] bg-bg-surface-v2 border-b border-border-v2 flex items-center px-6 gap-4 sticky top-0 z-40"
        >
          {/* [UI-TENANT] Busca global ainda não implementada — campo
              desabilitado com tooltip pra evitar a percepção de "input que
              não faz nada". Quando AB de busca chegar, basta tirar o
              disabled + plugar onChange/submit. */}
          <div
            className="flex items-center gap-2.5 w-[min(440px,38vw)] h-10 px-[13px] bg-bg-muted border border-transparent rounded-md opacity-60 cursor-not-allowed select-none"
            title="Busca global — em breve"
          >
            <IconSearch size={17} className="text-text-faint-v2 shrink-0" />
            <input
              type="search"
              placeholder="Buscar tenants, chamados…"
              disabled
              aria-disabled="true"
              className="flex-1 border-0 bg-transparent font-body text-[14px] text-text-primary placeholder:text-text-faint-v2 min-w-0 focus:outline-none cursor-not-allowed"
            />
            <kbd className="font-mono text-[10px] font-semibold text-text-tertiary bg-bg-surface-v2 border border-border-strong rounded px-1.5 py-0.5">
              Em breve
            </kbd>
          </div>

          <div className="ml-auto flex items-center gap-1">
            {/* [UI-F1] Sino real (drawer + lista + marcar-lido) já existe no
                componente `SinoNotificacoes`. Plugado aqui no modo
                `embedded` pra render como `IconButton` do topbar; o drawer
                continua sobrepondo a tela inteira como antes.
                [AB#415] Esta é a ÚNICA instância do sino — o floating global
                (App.tsx → SinoGlobal) foi removido: como TenantLayout e
                AdminLayout renderizam o AppShell, ele duplicava o botão (e o
                badge contava 2x) em toda rota autenticada. */}
            <SinoNotificacoes variant="embedded" />
            <IconButton ariaLabel="Alternar tema" onClick={toggleTheme}>
              {theme === 'light' ? <IconMoon size={20} /> : <IconSun size={20} />}
            </IconButton>
            {/* Gear vira atalho rápido pra configurações ([UI-PERFIL]/[UI-TENANT]):
                - SUPER_ADMIN → /admin/system-settings (configurações de sistema)
                - Tenant (ADMIN_TENANT, GESTOR, ATENDENTE etc.) → /settings/users
                  (gestão de equipe é o equivalente "configurações" do tenant) */}
            <IconButton
              ariaLabel="Configurações"
              onClick={() =>
                navigate(
                  usuario?.perfil === 'SUPER_ADMIN'
                    ? '/admin/system-settings'
                    : '/settings/users',
                )
              }
            >
              <IconSettings size={20} />
            </IconButton>
            <div className="w-px h-[26px] bg-border-v2 mx-1.5" />
            <UserChip
              nome={usuario?.nome ?? 'Convidado'}
              perfil={rotuloPerfil(usuario?.perfil ?? '', acesso)}
              email={usuario?.email ?? ''}
            />
          </div>
        </header>

        {/* SUBBAR — [Bug#830] SÓ a identidade do tenant (logo + nome), uma linha, altura fixa. O
            breadcrumb saiu daqui: vive no topo da área de conteúdo (abaixo), renderizado uma vez
            para todas as páginas. `empresaNome` vazio = SUPER_ADMIN (o store nunca carrega
            identidade pra esse perfil — EmpresaIdentidadeBoot pula o fetch) ou ainda carregando:
            a SUBBAR NÃO é renderizada.
            [Bug#773] `min-w-0` + `truncate` no nome: o texto corta com reticências (com `title`).
            [#916 reposicionamento] `justify-between`: nome do tenant à esquerda, seletor à
            direita (mesma faixa, alinhado embaixo do chip do usuário no topbar — sem linha
            nova). `flex-wrap` no container: quando não cabem lado a lado (sidebar expandida +
            nome longo + seletor com várias unidades), o CSS já quebra o seletor pra uma 2ª
            linha sozinho — sem precisar de breakpoint de viewport (a largura disponível aqui
            depende da sidebar estar aberta/colapsada, não só da tela). */}
        {empresaNome && (
          <div
            ref={subbarRef}
            className="min-h-[var(--subbar-h)] bg-bg-surface-v2 border-b border-border-v2 flex flex-wrap items-center justify-between gap-y-1 px-6 sticky z-[39]"
            style={{ top: 'var(--topbar-h)' }}
          >
            <div className="flex min-w-0 items-center gap-2">
              {empresaLogoUrl && (
                <img
                  src={resolverUrlEstatica(empresaLogoUrl)}
                  alt=""
                  aria-hidden="true"
                  className="h-7 w-7 shrink-0 rounded object-contain"
                />
              )}
              <span
                className="truncate text-[13px] font-semibold text-text-secondary"
                title={empresaNome}
              >
                {empresaNome}
              </span>
            </div>
            {/* [#916 reposicionamento] Seletor único de estabelecimento ativo — só nas telas
                onde a unidade afeta o dado exibido (ver seletor-estabelecimento-rotas.ts).
                Single-unit: o próprio componente não renderiza nada (transparente). Multi-unit
                fora da allowlist: também não renderiza — mesmo estado global, só não aparece
                aqui (a página em si não lê a unidade, não há nada pra filtrar). */}
            {deveMostrarSeletorEstabelecimento(pathname) && (
              <SeletorEstabelecimento
                value={estabelecimentoAtivoId}
                onChange={trocarEstabelecimentoAtivo}
                rotuloTodos="Todos"
                className="shrink-0"
              />
            )}
          </div>
        )}

        {/* CONTENT */}
        <main className="p-6 flex-1 min-w-0 bg-bg-app">
          {/* [Bug#830] Breadcrumb único do layout, no topo do conteúdo, à esquerda (mesma margem
              `p-6` do conteúdo). O `pb-4` faz parte da altura publicada em `--breadcrumb-real-h`. */}
          <div ref={breadcrumbRef} className="pb-4">
            <Breadcrumb trilha={breadcrumb} />
          </div>
          {/* [AB#1030] Erro de render na tela NÃO derruba o shell: menu, header e breadcrumb ficam
              fora do boundary. Reseta sozinho ao trocar de rota. */}
          <ErrorBoundaryDeRota>{children ?? <Outlet />}</ErrorBoundaryDeRota>
        </main>
      </div>
    </div>
  );
}

/* ===================================================================
 * SIDEBAR
 * ================================================================ */

interface SidebarProps {
  nav: NavItem[];
  brand: string;
  collapsed: boolean;
  pathname: string;
}

function Sidebar({ nav, brand, collapsed, pathname }: SidebarProps): JSX.Element {
  const { toggleCollapsed } = useSidebarCollapse();
  // [AB#174] Gating por módulo: itens/subitens com `modulo` definido só
  // aparecem quando o tenant tem o módulo. Itens sem `modulo` continuam
  // sempre visíveis (back-compat). Item-grupo que perde todos os children
  // pelo gate também desaparece (sem filhos → sem propósito).
  //
  // [FIX] Dependência do useMemo é o ARRAY `modulos`, NÃO a função
  // `temModulo`. Funções de Zustand mantêm referência estável — usar
  // `temModulo` na deps congelava `navFiltrada` no estado inicial
  // (modulos=[]) e o item "Vendas" nunca aparecia depois do store
  // carregar `mod_05`. Predicado é recriado inline a cada render em
  // que `modulos` muda.
  const modulos = useModulosStore((s) => s.modulos);
  // [F4.3 #890] Gating por permissão RBAC — mesmo desenho do módulo, composto com
  // ele (item precisa passar nos DOIS pra aparecer). `acesso` (não `pode`, que é uma
  // closure nova a cada render de `usePermissao()`) é a dependência do memo — mesmo
  // motivo do comentário acima sobre `modulos`.
  const { pode } = usePermissao();
  const acesso = useAcessoStore((s) => s.acesso);
  const navFiltrada = useMemo<NavItem[]>(() => {
    const tem = (codigo: string): boolean =>
      modulos.some((m) => m.codigo === codigo);
    const liberado = (item: { modulo?: string; permissao?: string | string[] }): boolean =>
      (!item.modulo || tem(item.modulo)) && (!item.permissao || pode(item.permissao));
    return nav
      .filter(liberado)
      .map((item) => {
        if (!item.children) return item;
        // Submenu sem nenhum item liberado some junto.
        const filhosVisiveis = item.children.flatMap((f): NavFilho[] => {
          if (!ehSubGrupo(f)) return liberado(f) ? [f] : [];
          const itens = f.itens.filter(liberado);
          return itens.length > 0 ? [{ ...f, itens }] : [];
        });
        return { ...item, children: filhosVisiveis };
      })
      .filter(
        (item) =>
          // item-grupo declarado com children acaba sem children depois
          // do gate → some também
          !item.children || item.children.length > 0,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav, modulos, acesso]);

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-50 flex flex-col py-4 bg-bg-sidebar-v2 overflow-hidden transition-[width] duration-[260ms] ease-[var(--ease)]',
      )}
      style={{ width: collapsed ? 'var(--sidebar-w-collapsed)' : 'var(--sidebar-w)' }}
    >
      {/* Brand — `mb-8` (32 px) cria respiro com o primeiro item da nav.
          [UI-F1] aumentado de mb-5 → mb-8 após reporte visual. */}
      <div className="flex items-center gap-3 h-10 px-[18px] mb-8 whitespace-nowrap">
        {/* Marca oficial. `shrink-0` mantém o ícone visível quando a sidebar
            colapsa — só o nome ao lado some (via `opacity-0`). Decorativa:
            `alt=""` + aria-hidden, porque o nome já vem no span seguinte. */}
        <img src="/logo.svg" alt="" className="h-7 w-7 shrink-0" aria-hidden="true" />
        <span
          className={cn(
            'font-display font-extrabold text-[18px] text-white tracking-tight transition-opacity duration-150',
            collapsed && 'opacity-0',
          )}
        >
          {brand}
        </span>
      </div>

      <nav className="flex-1 px-3 flex flex-col gap-0.5 overflow-y-auto">
        {navFiltrada.map((item) => (
          <SidebarItem key={item.key} item={item} collapsed={collapsed} pathname={pathname} />
        ))}
      </nav>

      {/* Footer — toggle de colapso */}
      <div className="mt-auto px-3">
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-label={collapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
          className="flex items-center gap-3 h-10 w-full px-3 rounded-md text-text-on-dark-dim hover:bg-bg-sidebar-hover hover:text-white transition-colors font-body text-[13px]"
        >
          <span className="w-[22px] grid place-items-center shrink-0">
            <IconLayoutSidebarLeftCollapse
              size={18}
              className={cn('transition-transform duration-[260ms]', collapsed && 'rotate-180')}
            />
          </span>
          <span className={cn('transition-opacity duration-150', collapsed && 'opacity-0')}>
            Colapsar
          </span>
        </button>
      </div>
    </aside>
  );
}

interface SidebarItemProps {
  item: NavItem;
  collapsed: boolean;
  pathname: string;
}

function SidebarItem({ item, collapsed, pathname }: SidebarItemProps): JSX.Element {
  const Icon = item.icon;
  const filhos = item.children ?? [];
  const hasChildren = filhos.length > 0;
  const { search } = useLocation();
  // Grupo abre quando algum filho casa (mesmo só pelo caminho — ex.: /financeiro/titulos sem `?direcao=`).
  const ativoFilho = hasChildren && folhasDoGrupo(filhos).some((c) => pontuarNav(pathname, search, c.path) > 0);
  const [open, setOpen] = useState<boolean>(ativoFilho);

  // [AB#219] Flyout flutuante dos filhos quando o sidebar está colapsado.
  // Aberto por HOVER no ícone pai e colado à direita do rail. Fica
  // `position: fixed` (escapa o `overflow-hidden` do <aside>) e ancorado no
  // topo do botão no viewport (getBoundingClientRect).
  const btnRef = useRef<HTMLButtonElement>(null);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [flyoutTop, setFlyoutTop] = useState(0);
  // Timer do fechamento com delay — evita piscar ao mover o mouse do ícone
  // para o flyout (cancelado se o mouse voltar antes dos 100ms).
  const closeTimer = useRef<number | null>(null);
  // Fecha o flyout quando o sidebar expande ou a rota muda (seleção de filho).
  useEffect(() => {
    if (!collapsed) setFlyoutOpen(false);
  }, [collapsed]);
  useEffect(() => {
    setFlyoutOpen(false);
  }, [pathname]);
  // Limpa o timer pendente no unmount.
  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    [],
  );

  if (!hasChildren && item.path) {
    return (
      <NavLink
        to={item.path}
        end={item.path === '/dashboard' || item.path === '/admin/dashboard'}
        className={({ isActive }) =>
          cn(
            'relative flex items-center gap-3 h-[42px] px-3 rounded-md font-body text-[14px] font-medium whitespace-nowrap transition-colors text-text-on-dark-dim hover:bg-bg-sidebar-hover hover:text-white',
            itemAtivo(pathname, search, item.path, isActive) && [
              'bg-bg-sidebar-active text-white',
              'before:content-[""] before:absolute before:-left-3 before:top-1/2 before:-translate-y-1/2 before:w-[3px] before:h-5 before:bg-primary before:rounded-r-sm',
            ],
          )
        }
      >
        <span className="w-[22px] grid place-items-center shrink-0">
          <Icon size={20} stroke={1.8} />
        </span>
        <span className={cn('transition-opacity duration-150', collapsed && 'opacity-0')}>
          {item.label}
        </span>
        {item.pill !== undefined && !collapsed && (
          <span className="ml-auto bg-danger-v2 text-white text-[11px] font-bold h-[19px] min-w-[19px] rounded-full grid place-items-center px-1.5">
            {item.pill}
          </span>
        )}
      </NavLink>
    );
  }

  // Item com submenu — accordion na sidebar aberta, flyout (hover) na colapsada.
  function cancelarFechamento(): void {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }
  function agendarFechamento(): void {
    cancelarFechamento();
    closeTimer.current = window.setTimeout(() => setFlyoutOpen(false), 100);
  }
  function abrirFlyout(): void {
    if (!collapsed) return;
    cancelarFechamento();
    const rect = btnRef.current?.getBoundingClientRect();
    setFlyoutTop(rect?.top ?? 0);
    setFlyoutOpen(true);
  }

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        // Colapsado: hover abre o flyout. Expandido: clique alterna o accordion
        // (comportamento original inalterado).
        onClick={() => {
          if (!collapsed) setOpen((v) => !v);
        }}
        onMouseEnter={abrirFlyout}
        onMouseLeave={agendarFechamento}
        aria-expanded={collapsed ? flyoutOpen : open}
        aria-haspopup={collapsed ? 'menu' : undefined}
        className={cn(
          'flex items-center gap-3 h-[42px] w-full px-3 rounded-md text-left font-body text-[14px] font-medium whitespace-nowrap transition-colors',
          ativoFilho || (collapsed && flyoutOpen)
            ? 'text-white'
            : 'text-text-on-dark-dim hover:bg-bg-sidebar-hover hover:text-white',
        )}
      >
        <span className="w-[22px] grid place-items-center shrink-0">
          <Icon size={20} stroke={1.8} />
        </span>
        <span className={cn('transition-opacity duration-150', collapsed && 'opacity-0')}>
          {item.label}
        </span>
        <IconChevronRight
          size={15}
          stroke={2.2}
          className={cn(
            'ml-auto transition-[transform,opacity] duration-200',
            open && !collapsed && 'rotate-90',
            collapsed && 'opacity-0',
          )}
        />
      </button>

      {/* Accordion — só visível com sidebar aberta */}
      {!collapsed && (
        <div
          className={cn(
            'overflow-hidden flex flex-col gap-px transition-[max-height] duration-[260ms]',
            open ? 'max-h-[900px]' : 'max-h-0',
          )}
        >
          {filhos.map((f) =>
            ehSubGrupo(f) ? (
              <SubGrupo key={f.key} grupo={f} pathname={pathname} />
            ) : (
              <SubItem key={f.key} sub={f} pathname={pathname} />
            ),
          )}
        </div>
      )}

      {/* [AB#219] Flyout — só na colapsada, aberto por hover. `fixed` pra
          escapar do `overflow-hidden` do <aside>; colado à direita do rail.
          Hover no flyout mantém aberto; sair dele agenda o fechamento. */}
      {collapsed && flyoutOpen && (
        <SidebarFlyout
          item={item}
          top={flyoutTop}
          pathname={pathname}
          onMouseEnter={cancelarFechamento}
          onMouseLeave={agendarFechamento}
          onClose={() => setFlyoutOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * [AB#219] Flyout flutuante dos itens-filho, exibido ao passar o mouse num
 * menu pai com o sidebar colapsado (icon rail). `position: fixed` colado à
 * direita do rail (left = largura colapsada, sem gap) e no topo do ícone
 * (`top`), pra não ser recortado pelo `overflow-hidden` do <aside>. Mantém-se
 * aberto enquanto o mouse está sobre ele (onMouseEnter cancela o fechamento);
 * sair agenda o fechamento (delay no pai). Fecha também ao apertar Esc ou
 * selecionar um filho — sem expandir o sidebar. Cores do sidebar (não branco).
 */
function SidebarFlyout({
  item,
  top,
  pathname,
  onMouseEnter,
  onMouseLeave,
  onClose,
}: {
  item: NavItem;
  top: number;
  pathname: string;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClose: () => void;
}): JSX.Element {
  const { search } = useLocation();
  useEffect(() => {
    function aoKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', aoKeyDown);
    return () => document.removeEventListener('keydown', aoKeyDown);
  }, [onClose]);

  return (
    <div
      role="menu"
      aria-label={item.label}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{ top, left: 'var(--sidebar-w-collapsed)' }}
      className="fixed z-50 w-fit min-w-[160px] max-w-[220px] rounded-md bg-bg-sidebar-v2 p-1 shadow-lg ring-1 ring-black/25"
    >
      {/* [AB#219] Sem cabeçalho com o label do pai — o flyout mostra apenas os
          filhos. O nome do grupo já está no aria-label do container. */}
      {(item.children ?? []).map((f) =>
        ehSubGrupo(f) ? (
          <Fragment key={f.key}>
            <SecaoTitulo texto={f.label} flyout />
            {f.itens.map((sub) => (
              <FlyoutLink key={sub.key} sub={sub} pathname={pathname} search={search} onClose={onClose} recuo />
            ))}
          </Fragment>
        ) : (
          <FlyoutLink key={f.key} sub={f} pathname={pathname} search={search} onClose={onClose} />
        ),
      )}
    </div>
  );
}

/** Cabeçalho (não clicável) de submenu dentro do flyout do menu colapsado. */
function SecaoTitulo({ texto, flyout = false }: { texto: string; flyout?: boolean }): JSX.Element {
  return (
    <div
      role="presentation"
      className={cn(
        'pt-2 pb-0.5 font-body text-[11px] font-semibold uppercase tracking-wide text-text-on-dark-dim opacity-70',
        flyout ? 'px-2.5' : 'pl-12 pr-3',
      )}
    >
      {texto}
    </div>
  );
}

function FlyoutLink({
  sub,
  pathname,
  search,
  onClose,
  recuo = false,
}: {
  sub: NavSubItem;
  pathname: string;
  search: string;
  onClose: () => void;
  recuo?: boolean;
}): JSX.Element {
  return (
    <Link
      to={sub.path}
      role="menuitem"
      onClick={onClose}
      className={cn(
        'block rounded-sm px-2.5 py-2 font-body text-[13px] text-text-on-dark-dim transition-colors hover:bg-bg-sidebar-hover hover:text-white',
        recuo && 'pl-4',
        itemAtivo(pathname, search, sub.path) && 'bg-bg-sidebar-active text-white',
      )}
    >
      {sub.label}
    </Link>
  );
}

function lerSubmenu(chave: string): boolean | null {
  try {
    const v = window.localStorage.getItem(chave);
    return v === '1' ? true : v === '0' ? false : null;
  } catch {
    return null;
  }
}

/**
 * Submenu colapsável (ex.: Financeiro › Contas a receber). Abre sozinho quando a rota ativa está dentro dele; fora isso,
 * respeita a última escolha do usuário (lembrada em localStorage, com try/catch — sem ele, só abre quando contém a rota).
 */
export function SubGrupo({ grupo, pathname }: { grupo: NavSubGrupo; pathname: string }): JSX.Element {
  const { search } = useLocation();
  const usuarioId = useAuthStore((s) => s.usuario?.id);
  const chave = chaveSubmenu(usuarioId, grupo.key);
  const ativo = grupo.itens.some((i) => pontuarNav(pathname, search, i.path) === 2);
  const [aberto, setAberto] = useState<boolean>(() => ativo || (lerSubmenu(chave) ?? false));
  useEffect(() => {
    if (ativo) setAberto(true);
  }, [ativo, pathname, search]);
  function alternar(): void {
    const novo = !aberto;
    setAberto(novo);
    try {
      window.localStorage.setItem(chave, novo ? '1' : '0');
    } catch {
      /* sem armazenamento: o estado vale só na sessão */
    }
  }
  return (
    <div>
      <button
        type="button"
        onClick={alternar}
        aria-expanded={aberto}
        className={cn(
          'flex h-9 w-full items-center gap-2 rounded-md pl-12 pr-3 text-left font-body text-[13px] whitespace-nowrap transition-colors hover:bg-bg-sidebar-hover hover:text-white',
          ativo ? 'text-white' : 'text-text-on-dark-dim',
        )}
      >
        {grupo.label}
        <IconChevronRight size={13} stroke={2.2} className={cn('ml-auto transition-transform duration-200', aberto && 'rotate-90')} />
      </button>
      {aberto && (
        <div className="flex flex-col gap-px" role="group" aria-label={grupo.label}>
          {grupo.itens.map((sub) => (
            <SubItem key={sub.key} sub={sub} pathname={pathname} recuado />
          ))}
        </div>
      )}
    </div>
  );
}

function SubItem({ sub, pathname, recuado = false }: { sub: NavSubItem; pathname: string; recuado?: boolean }): JSX.Element {
  const { search } = useLocation();
  return (
    <NavLink
      to={sub.path}
      className={({ isActive }) =>
        cn(
          'relative flex items-center gap-2.5 h-9 pr-3 rounded-md font-body text-[13px] whitespace-nowrap text-text-on-dark-dim hover:bg-bg-sidebar-hover hover:text-white transition-colors',
          recuado ? 'pl-[4.5rem] before:left-12' : 'pl-12 before:left-6',
          'before:content-[""] before:absolute before:w-1.5 before:h-1.5 before:rounded-full before:bg-current before:opacity-45',
          itemAtivo(pathname, search, sub.path, isActive) &&
            'text-white before:opacity-100 before:bg-primary',
        )
      }
    >
      {sub.label}
    </NavLink>
  );
}

/* ===================================================================
 * TOPBAR utilities
 * ================================================================ */

interface IconButtonProps {
  children: ReactNode;
  ariaLabel: string;
  onClick?: () => void;
}

function IconButton({ children, ariaLabel, onClick }: IconButtonProps): JSX.Element {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      className="w-10 h-10 rounded-md grid place-items-center bg-transparent text-text-secondary hover:bg-bg-muted hover:text-text-primary transition-colors relative"
    >
      {children}
    </button>
  );
}

interface UserChipProps {
  nome: string;
  perfil: string;
  email: string;
}

function UserChip({ nome, perfil, email }: UserChipProps): JSX.Element {
  const navigate = useNavigate();
  const logoutStore = useAuthStore((s) => s.logout);

  // [UI-TENANT] "Meu perfil" leva pra rota correta conforme o role.
  const perfilPath =
    perfil === 'SUPER_ADMIN' ? '/admin/perfil' : '/minha-conta/perfil';

  async function sair(): Promise<void> {
    await logoutStore();
    broadcastAuth('LOGOUT');
    navigate('/login');
  }

  return (
    <Dropdown
      align="right"
      trigger={
        <button
          type="button"
          aria-label="Abrir menu do usuário"
          className="flex items-center gap-2.5 h-[42px] pl-1.5 pr-2 rounded-md hover:bg-bg-muted transition-colors ml-0.5"
        >
          <Avatar name={nome} size="md" />
          <div className="flex flex-col leading-tight text-left">
            <span className="text-[13px] font-semibold text-text-primary">{nome}</span>
            <span className="text-[11px] text-text-tertiary">{perfil}</span>
          </div>
          <IconSelector size={16} className="text-text-faint-v2" />
        </button>
      }
    >
      {/* Cabeçalho — não clicável. Não usa MenuItem porque MenuItem é
          interativo; uso um div estilizado pra mostrar contexto do usuário. */}
      <div className="px-3 py-2.5 flex items-center gap-3 min-w-[260px]">
        <Avatar name={nome} size="lg" />
        <div className="flex flex-col leading-tight min-w-0">
          <span className="text-[13px] font-semibold text-text-primary truncate">{nome}</span>
          {email && (
            <span className="text-[12px] text-text-tertiary truncate">{email}</span>
          )}
          <span className="text-[11px] text-text-faint-v2">{perfil}</span>
        </div>
      </div>
      <MenuSeparator />
      <MenuItem
        icon={<IconUser size={16} />}
        onClick={() => navigate(perfilPath)}
      >
        Meu perfil
      </MenuItem>
      <MenuSeparator />
      <MenuItem
        icon={<IconLogout size={16} />}
        variant="danger"
        onClick={() => void sair()}
      >
        Sair
      </MenuItem>
    </Dropdown>
  );
}

/* ===================================================================
 * Breadcrumb
 * ================================================================ */

const ChevronCrumb = (): JSX.Element => (
  <svg
    aria-hidden="true"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-border-strong"
  >
    <path d="M9 18l6-6-6-6" />
  </svg>
);

function Breadcrumb({ trilha }: { trilha: TrilhaItem[] }): JSX.Element {
  return (
    <nav aria-label="Trilha de navegação" className="flex items-center gap-2 text-[14px]">
      {trilha.map((c, i) => {
        const isLast = i === trilha.length - 1;
        const content = (
          <span
            className={cn(
              isLast
                ? 'text-text-primary font-semibold'
                : 'text-text-tertiary font-medium',
              !isLast && c.path && 'cursor-pointer hover:text-text-secondary',
            )}
          >
            {c.label}
          </span>
        );
        return (
          <span key={`${c.label}-${i}`} className="flex items-center gap-2">
            {!isLast && c.path ? <Link to={c.path}>{content}</Link> : content}
            {!isLast && (
              <span aria-hidden="true">
                <ChevronCrumb />
              </span>
            )}
          </span>
        );
      })}
    </nav>
  );
}
